// The runs started from this Generate session, under the contact sheet:
// every expected video of every run is a small tile (half a photo tile)
// behind a rule, so they read as "what you made" rather than "what you are
// about to send". A finished video plays muted in its tile; one still on
// its way shows its state. A click on a video opens a quick viewer over the
// page: that video large, the session's other videos as a contact sheet
// under it; a click anywhere else, or Esc, closes it.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getArtifact, getTask, type TaskView } from "../api/client";
import { storeSrc } from "../api/store";
import type { SessionRun } from "../lib/genSession";

const POLL_MS = 5_000;
const TERMINAL = new Set(["DONE", "FAILED", "CANCELLED"]);

// Survive leaving and re-entering the tab, so tiles do not flash empty.
const views = new Map<string, TaskView>();
const srcOf = new Map<string, string>();

interface Tile {
  key: string;
  runId: string;
  state: string; // slot state, lower case
  artifactId?: string;
  prompt?: string; // what the clip runs with (enhanced, ADR-0055)
}

function tilesOf(run: SessionRun, v: TaskView | undefined): Tile[] {
  const expected = v?.requested ?? run.results ?? 1;
  return Array.from({ length: expected }, (_, i) => {
    const slot = v?.results?.[i];
    const state = (slot?.state ?? (v ? v.state : "QUEUED")).toLowerCase();
    return {
      key: `${run.taskId}/${i}`,
      runId: run.taskId,
      state,
      artifactId: slot?.state === "DONE" ? slot.artifact_id : undefined,
      prompt: slot?.prompt,
    };
  });
}

/** The run's prompt when all its clips share one (one photo, any variants). */
function runPrompt(tiles: Tile[]): string | undefined {
  const prompts = new Set(tiles.map((t) => t.prompt).filter((p): p is string => !!p));
  return prompts.size === 1 ? [...prompts][0] : undefined;
}

export default function RunStrip({ runs }: { runs: SessionRun[] }) {
  const [, setTick] = useState(0);
  const [viewing, setViewing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    async function poll() {
      for (const r of runs) {
        const known = views.get(r.taskId);
        if (known && TERMINAL.has(known.state) && (known.results ?? []).every((s) => !s.artifact_id || srcOf.has(s.artifact_id))) continue;
        try {
          const next = await getTask(r.taskId);
          views.set(r.taskId, next);
          for (const s of next.results ?? []) {
            if (s.state !== "DONE" || !s.artifact_id || srcOf.has(s.artifact_id)) continue;
            const src = storeSrc(await getArtifact(s.artifact_id));
            if (src) srcOf.set(s.artifact_id, src);
          }
        } catch {
          /* a run that cannot be read keeps its last state */
        }
      }
      if (live) setTick((t) => t + 1);
    }
    void poll();
    const timer = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [runs]);

  const ordered = useMemo(() => [...runs].reverse(), [runs]);
  const done = ordered.flatMap((r) => tilesOf(r, views.get(r.taskId))).filter((t) => t.artifactId && srcOf.has(t.artifactId));

  useEffect(() => {
    if (!viewing) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setViewing(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewing]);

  if (runs.length === 0) return null;
  return (
    <section className="u-runs" aria-label="Runs from this session">
      <h2 className="u-runs__title">Runs</h2>
      <div className="u-runs__list" data-testid="run-strip">
        {ordered.map((r) => {
          const v = views.get(r.taskId);
          const state = (v?.state ?? "QUEUED").toLowerCase();
          const tiles = tilesOf(r, v);
          const prompt = runPrompt(tiles);
          return (
            <div key={r.taskId} className="u-run" data-testid="run">
              <div className="u-run__head">
                <Link to={`/tasks/${r.taskId}`} className="u-run__label" title="Open this run">
                  {r.label}
                </Link>
                <span className={`u-state u-state--${state}`}>{state}</span>
              </div>
              {prompt && (
                <p className="u-run__prompt" data-testid="run-prompt" title={prompt}>
                  {prompt}
                </p>
              )}
              <ul className="u-runs__grid">
                {tiles.map((t) => {
                  const src = t.artifactId ? srcOf.get(t.artifactId) : undefined;
                  return (
                    <li key={t.key} className={`u-clip u-clip--${t.state}`} data-testid="clip" title={t.prompt}>
                      {src ? (
                        <button className="u-clip__btn" onClick={() => setViewing(t.artifactId as string)} title="Play large">
                          <video src={src} muted loop autoPlay playsInline preload="metadata" />
                        </button>
                      ) : (
                        <span className="u-clip__wait">
                          {t.state}
                          {!TERMINAL.has(t.state.toUpperCase()) && <span className="u-run__pulse" aria-hidden="true" />}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {viewing && srcOf.get(viewing) && (
        // The quick viewer: any click that is not on a video closes it.
        <div className="u-lightbox" data-testid="lightbox" onClick={() => setViewing(null)} role="dialog" aria-label="Video">
          <video
            className="u-lightbox__main"
            src={srcOf.get(viewing)}
            autoPlay
            loop
            muted
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
          <Link
            className="u-btn u-lightbox__cut"
            data-testid="lightbox-cut"
            to={`/library/cut/${viewing}`}
            onClick={(e) => e.stopPropagation()}
          >
            Cut…
          </Link>
          {done.find((t) => t.artifactId === viewing)?.prompt && (
            <p className="u-lightbox__prompt" data-testid="lightbox-prompt" onClick={(e) => e.stopPropagation()}>
              {done.find((t) => t.artifactId === viewing)?.prompt}
            </p>
          )}
          <ul className="u-lightbox__sheet">
            {done.map((t) => (
              <li key={t.key}>
                <button
                  className={t.artifactId === viewing ? "u-lightbox__thumb u-lightbox__thumb--on" : "u-lightbox__thumb"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewing(t.artifactId as string);
                  }}
                >
                  <video src={srcOf.get(t.artifactId as string)} muted loop autoPlay playsInline preload="metadata" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
